// lib/vividworks.helpers.ts
import type {
  VwAddToBasketPayload,
  VwComposition,
  VwModule,
  VwTotal,
} from './types'

// ---- Your internal cart model (adjust to your app) ----
export interface CartLine {
  sku: string
  qty: number
  name?: string
  image?: string
  attributes?: Record<string, string | number>
  meta?: Record<string, unknown>
}

export interface CartBundle {
  id: string // plan.id (quote/config id)
  title: string // e.g. composition name or plan id
  lines: CartLine[]
  measures?: number[] // optional helpful data
  shareLink?: string
  unitSystem?: string
}

/**
 * Prefer module.codes |> composition.totals as SKU sources.
 * This yields clean lines like [{ sku: "10143699", qty: 1 }, ...]
 */
export function mapVividworksToCart(
  payload: VwAddToBasketPayload,
): CartBundle[] {
  const bundles: CartBundle[] = []

  for (const comp of payload.compositions) {
    const lines: CartLine[] = []
    if (!comp) continue
    if (!('modules' in comp)) continue
    if (!Array.isArray(comp.modules)) continue

    // 1) Pull SKUs from modules. Prefer explicit codes, fall back to totals.
    comp.modules.forEach((m) => {
      const preferredTotals = m.codes && m.codes.length > 0 ? m.codes : m.totals
      mergeOrAppend(lines, toLinesFromTotals(preferredTotals, m))
    })

    // 2) If we didn't get anything from modules, use composition totals.
    if (lines.length === 0) {
      mergeOrAppend(lines, toLinesFromTotals(comp.totals, comp))
    } else {
      appendMissing(lines, toLinesFromTotals(comp.totals, comp))
    }

    // 3) Build the bundle
    bundles.push({
      id: payload.id,
      title: comp.name ?? payload.id,
      lines,
      measures: comp.measures,
      shareLink: payload.shareLink,
      unitSystem: payload.unitSystem,
    })
  }

  return bundles
}

// ---- helpers ----

function toLinesFromTotals(
  totals: VwTotal[] | undefined,
  owner: VwModule | VwComposition,
): CartLine[] {
  if (!totals || totals.length === 0) return []
  const base: Partial<CartLine> =
    'thumbnail' in owner
      ? { image: owner.thumbnail, name: owner.name }
      : { name: 'name' in owner ? owner.name : undefined }

  return totals.map((t) => ({
    sku: t.code,
    qty: resolveQuantity(t),
    ...base,
    attributes: extractAttributes(owner),
    meta: {
      unitKind: t.unitOfMeasureData?.unitKind,
      formattedValue: t.unitOfMeasureData?.formattedValue,
      feature: 'feature' in owner ? owner.feature : undefined,
      moduleKey: 'key' in owner ? owner.key : undefined,
    },
  }))
}

function extractAttributes(
  owner: VwModule | VwComposition,
): Record<string, string | number> {
  // Combine owner.features and e.g. first material as attributes
  const attrs: Record<string, string | number> = { ...(owner.features ?? {}) }
  if (
    'materials' in owner &&
    Array.isArray(owner.materials) &&
    owner.materials.length > 0
  ) {
    const m = owner.materials[0]
    attrs['material'] = m?.name
  }
  return attrs
}

function mergeOrAppend(target: CartLine[], incoming: CartLine[]) {
  for (const line of incoming) {
    const idx = target.findIndex((x) => x.sku === line.sku)
    if (idx >= 0) {
      target[idx].qty += line.qty
    } else {
      target.push(line)
    }
  }
}

function appendMissing(target: CartLine[], incoming: CartLine[]) {
  incoming.forEach((line) => {
    if (!target.some((existing) => existing.sku === line.sku)) {
      target.push(line)
    }
  })
}

function resolveQuantity(total: VwTotal): number {
  const unitKind = total.unitOfMeasureData?.unitKind
  if (unitKind === 'Count') {
    const converted = total.unitOfMeasureData?.convertedAmount
    if (typeof converted === 'number' && !Number.isNaN(converted)) {
      return converted
    }

    const formatted = total.unitOfMeasureData?.formattedValue
    if (formatted) {
      const parsed = Number.parseFloat(formatted)
      if (!Number.isNaN(parsed)) {
        return parsed
      }
    }
  }

  return total.amount
}

// ---- embed init (typed) ----

type VividworksInitOptions = {
  addToBasket: (
    bundles: CartBundle[],
    raw: VwAddToBasketPayload,
  ) => void | Promise<void>
}

/**
 * Initializes Vividworks embed in a typed, React-friendly way.
 * Avoids stringly-typed Script and keeps your callback fully typed.
 */
export async function initVividworks({ addToBasket }: VividworksInitOptions) {
  // client-side only
  if (typeof window === 'undefined') return

  // Dynamic ESM import of the embed library (remote ESM). Types provided via ambient module decl.
  const { default: vw3d } = await import(
    /* webpackIgnore: true */
    'https://app.vividworks.com/embed/vividworks-frontend-embed.es.js'
  )

  const resp = await vw3d({
    addToBasketCallback: (item: VwAddToBasketPayload) => {
      const bundles = mapVividworksToCart(item)
      return addToBasket(bundles, item)
    },
  })

  // optionally expose for debugging, as you did:
  ;(window as any).vw3d = resp
}
